import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import { PulseLoader } from '../../../common/PulseLoader';

const messages = defineMessages({
  toggle: {
    id: 'ActionToolkit.toggle',
    defaultMessage: 'Take another action',
  },
  heading: {
    id: 'ActionToolkit.heading',
    defaultMessage: 'Action toolkit',
  },
  description: {
    id: 'ActionToolkit.description',
    defaultMessage: 'Trigger an action directly instead of the suggested one.',
  },
  loadError: {
    id: 'ActionToolkit.loadError',
    defaultMessage: 'Could not load the action toolkit.',
  },
  comingSoon: {
    id: 'ActionToolkit.comingSoon',
    defaultMessage: 'Coming soon',
  },
  send: {
    id: 'ActionToolkit.send',
    defaultMessage: 'Send',
  },
  sending: {
    id: 'ActionToolkit.sending',
    defaultMessage: 'Sending…',
  },
  sent: {
    id: 'ActionToolkit.sent',
    defaultMessage: 'Sent',
  },
  selectRecipient: {
    id: 'ActionToolkit.selectRecipient',
    defaultMessage: 'Select a care-team member…',
  },
  noRecipients: {
    id: 'ActionToolkit.noRecipients',
    defaultMessage: 'No care-team members with a contact address were found.',
  },
  actionSuccess: {
    id: 'ActionToolkit.actionSuccess',
    defaultMessage: 'Action completed.',
  },
  actionError: {
    id: 'ActionToolkit.actionError',
    defaultMessage: 'Could not complete the action. Please try again.',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.pxToRem(12),
  },
  panel: {
    marginTop: theme.pxToRem(8),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.pxToRem(8),
  },
  actionCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    padding: theme.pxToRem(12),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.pxToRem(6),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.pxToRem(4),
  },
  input: {
    width: '100%',
    padding: theme.pxToRem(6),
    borderRadius: theme.pxToRem(4),
    border: `1px solid ${theme.palette.divider}`,
    font: 'inherit',
  },
  textarea: {
    width: '100%',
    minHeight: theme.pxToRem(72),
    padding: theme.pxToRem(6),
    borderRadius: theme.pxToRem(4),
    border: `1px solid ${theme.palette.divider}`,
    font: 'inherit',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.pxToRem(6),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.pxToRem(8),
  },
}));

type ActionInput = {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'boolean' | 'recipient-email';
  required: boolean;
  placeholder?: string;
};

type ActionDefinition = {
  actionKey: string;
  label: string;
  description: string;
  status: 'available' | 'coming-soon';
  inputs: ActionInput[];
};

type Recipient = {
  practitionerId: string;
  name: string;
  role?: string;
  email?: string;
};

type FieldValues = Record<string, string | boolean>;

export type ActionToolkitProps = {
  patientId: string;
  issueId?: string;
};

const isFilled = (value: string | boolean | undefined): boolean =>
  typeof value === 'string' ? value.trim() !== '' : Boolean(value);

const ActionToolkitForm: React.FC<{
  action: ActionDefinition;
  recipients: Recipient[];
  patientId: string;
  issueId?: string;
  onActionComplete: () => void;
}> = ({ action, recipients, patientId, issueId, onActionComplete }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [notify] = useNotify();
  const [values, setValues] = useState<FieldValues>({});
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const messageMemberMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/actions/message-member',
  );
  const messageCareTeamMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/actions/message-care-team',
  );
  const messageClinicianMutation = apiHooks.useAPIMutation(
    'POST /v1/member-operations/priorities/members/:patientId/actions/message-clinician',
  );

  const emailRecipients = useMemo(
    () =>
      recipients.filter(
        (recipient): recipient is Recipient & { email: string } =>
          Boolean(recipient.email),
      ),
    [recipients],
  );

  const setValue = (name: string, value: string | boolean) =>
    setValues(current => ({ ...current, [name]: value }));

  const stringValue = (name: string): string => {
    const value = values[name];
    return typeof value === 'string' ? value : '';
  };

  const requiredMet = action.inputs
    .filter(input => input.required)
    .every(input => isFilled(values[input.name]));

  // A recipient-email action is unusable when no care-team member has a
  // contact address to target.
  const blockedOnRecipients =
    action.inputs.some(input => input.type === 'recipient-email') &&
    emailRecipients.length === 0;

  const sendDisabled =
    isSending || isSent || !requiredMet || blockedOnRecipients;

  const handleComplete = () => {
    setIsSent(true);
    notify(formatMessage(messages.actionSuccess), 'success');
    onActionComplete();
  };
  const handleError = () => {
    notify(formatMessage(messages.actionError), 'error');
  };

  const submit = () => {
    setIsSending(true);
    const callbacks = {
      onSuccess: handleComplete,
      onError: handleError,
      onSettled: () => setIsSending(false),
    };

    if (action.actionKey === 'message-member') {
      messageMemberMutation.mutate(
        {
          patientId,
          title: stringValue('title'),
          message: stringValue('message'),
          issueId,
        },
        callbacks,
      );
      return;
    }
    if (action.actionKey === 'message-care-team') {
      messageCareTeamMutation.mutate(
        {
          patientId,
          message: stringValue('message'),
          urgent: values.urgent === true,
          issueId,
        },
        callbacks,
      );
      return;
    }
    messageClinicianMutation.mutate(
      {
        patientId,
        email: stringValue('email'),
        message: stringValue('message'),
        urgent: values.urgent === true,
        issueId,
      },
      callbacks,
    );
  };

  return (
    <div
      className={classes.actionCard}
      data-testid={`toolkit-action-${action.actionKey}`}
    >
      <Text size="subbody" weight="bold">
        {action.label}
      </Text>
      <Text size="subbody">{action.description}</Text>
      {action.inputs.map(input => {
        const fieldTestId = `toolkit-field-${action.actionKey}-${input.name}`;
        if (input.type === 'boolean') {
          return (
            <label className={classes.checkboxRow} key={input.name}>
              <input
                type="checkbox"
                checked={values[input.name] === true}
                disabled={isSending || isSent}
                onChange={e => setValue(input.name, e.target.checked)}
                data-testid={fieldTestId}
              />
              <Text size="subbody">{input.label}</Text>
            </label>
          );
        }
        if (input.type === 'recipient-email') {
          return (
            <div className={classes.field} key={input.name}>
              <Text size="subbody">{input.label}</Text>
              {emailRecipients.length === 0 ? (
                <Text size="subbody">
                  {formatMessage(messages.noRecipients)}
                </Text>
              ) : (
                // Controlled select: onChange is the correct commit handler.
                // eslint-disable-next-line jsx-a11y/no-onchange
                <select
                  className={classes.input}
                  aria-label={input.label}
                  value={stringValue(input.name)}
                  disabled={isSending || isSent}
                  onChange={e => setValue(input.name, e.target.value)}
                  data-testid={fieldTestId}
                >
                  <option value="">
                    {formatMessage(messages.selectRecipient)}
                  </option>
                  {emailRecipients.map(recipient => (
                    <option
                      key={recipient.practitionerId}
                      value={recipient.email}
                    >
                      {recipient.role
                        ? `${recipient.name} (${recipient.role})`
                        : recipient.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        }
        if (input.type === 'textarea') {
          return (
            <div className={classes.field} key={input.name}>
              <Text size="subbody">{input.label}</Text>
              <textarea
                className={classes.textarea}
                aria-label={input.label}
                value={stringValue(input.name)}
                placeholder={input.placeholder}
                disabled={isSending || isSent}
                onChange={e => setValue(input.name, e.target.value)}
                data-testid={fieldTestId}
              />
            </div>
          );
        }
        return (
          <div className={classes.field} key={input.name}>
            <Text size="subbody">{input.label}</Text>
            <input
              type="text"
              className={classes.input}
              aria-label={input.label}
              value={stringValue(input.name)}
              placeholder={input.placeholder}
              disabled={isSending || isSent}
              onChange={e => setValue(input.name, e.target.value)}
              data-testid={fieldTestId}
            />
          </div>
        );
      })}
      <Button
        variant="contained"
        color="default"
        onClick={submit}
        disabled={sendDisabled}
        data-testid={`toolkit-send-${action.actionKey}`}
      >
        {formatMessage(
          isSent ? messages.sent : isSending ? messages.sending : messages.send,
        )}
      </Button>
    </div>
  );
};

/**
 * Clinician-initiated "action toolkit" for the Zori Pulse critical-issue view.
 * Lets a reviewer trigger an action (message the member, the care team, or a
 * specific care-team clinician) directly instead of the AI-suggested one. The
 * catalog and its per-action input schema come from member-operations-service
 * so the UI renders forms generically and stays in sync as actions evolve.
 */
export const ActionToolkit: React.FC<ActionToolkitProps> = ({
  patientId,
  issueId,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const [isOpen, setIsOpen] = useState(false);

  const toolkitQuery = apiHooks.useAPIQuery(
    'GET /v1/member-operations/priorities/members/:patientId/action-toolkit',
    { patientId },
    { enabled: isOpen },
  );

  return (
    <div className={classes.root} data-testid="action-toolkit">
      <Button
        variant="text"
        color="default"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        data-testid="action-toolkit-toggle"
      >
        {formatMessage(messages.toggle)}
      </Button>

      {isOpen ? (
        <div className={classes.panel}>
          <div className={classes.headerRow}>
            <Text size="subbody" weight="bold">
              {formatMessage(messages.heading)}
            </Text>
          </div>
          <Text size="subbody">{formatMessage(messages.description)}</Text>

          {toolkitQuery.isLoading ? (
            <Box align="center" data-testid="action-toolkit-loading">
              <PulseLoader width={48} />
            </Box>
          ) : toolkitQuery.isError || toolkitQuery.data == null ? (
            <Text size="subbody" data-testid="action-toolkit-error">
              {formatMessage(messages.loadError)}
            </Text>
          ) : (
            toolkitQuery.data.actions.map(action =>
              action.status === 'coming-soon' ? (
                <div
                  className={classes.actionCard}
                  key={action.actionKey}
                  data-testid={`toolkit-action-${action.actionKey}`}
                >
                  <div className={classes.headerRow}>
                    <Text size="subbody" weight="bold">
                      {action.label}
                    </Text>
                    <Pill
                      label={formatMessage(messages.comingSoon)}
                      data-testid={`toolkit-coming-soon-${action.actionKey}`}
                    />
                  </div>
                  <Text size="subbody">{action.description}</Text>
                </div>
              ) : (
                <ActionToolkitForm
                  key={action.actionKey}
                  action={action}
                  recipients={toolkitQuery.data.recipients}
                  patientId={patientId}
                  issueId={issueId}
                  onActionComplete={() => undefined}
                />
              ),
            )
          )}
        </div>
      ) : null}
    </div>
  );
};
